import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, UserPlus } from "lucide-react";
import api from "../lib/api";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLE_LABEL = { super_admin: "Super Admin", client_admin: "Client Admin", manager: "Manager", employee: "Employee" };
const ROLE_STYLE = {
  super_admin: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
  client_admin: "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]",
  manager: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  employee: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
};

export default function Team() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "employee", password: "Welcome!2026" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.name || !form.email) return;
    try {
      await api.post("/users/invite", form);
      toast.success(`${form.name} invited`);
      setOpen(false); setForm({ name: "", email: "", role: "employee", password: "Welcome!2026" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to invite user");
    }
  };

  if (!users) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;

  return (
    <div>
      <PageHeader title="Team" subtitle="Manage users and roles across your organisation.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="invite-user-btn"><Plus className="mr-2 h-4 w-4" /> Invite User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Invite User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client_admin">Client Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="invite-password" /></div>
            </div>
            <DialogFooter><Button onClick={invite} className="rounded-full" data-testid="invite-save-btn"><UserPlus className="mr-2 h-4 w-4" /> Send Invite</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="overflow-hidden rounded-2xl border-border">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between border-b border-border/60 px-5 py-3.5 last:border-0" data-testid={`user-row-${u.email}`}>
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9"><AvatarFallback className="bg-secondary text-sm">{u.name?.[0]}</AvatarFallback></Avatar>
              <div><div className="text-sm font-medium">{u.name}</div><div className="text-xs text-muted-foreground">{u.email}</div></div>
            </div>
            <Badge className={`rounded-full ${ROLE_STYLE[u.role]}`}>{ROLE_LABEL[u.role]}</Badge>
          </div>
        ))}
      </Card>
    </div>
  );
}
